import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CalculatorLuxury from '../components/CalculatorLuxury';
import { Shield, Zap, Users, Star, Clock, CheckCircle, ArrowRight, Instagram, Facebook, ChevronDown, Calculator, Send, HeartHandshake, Smartphone, Building2, Sparkles } from 'lucide-react';
import { BRAND } from '../brand';

const LandingBrand = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = BRAND.landing.faqs.map((f) => ({
    q: f.q.replace(/\$\{BRAND\.name\}/g, BRAND.name),
    a: f.a.replace(/\$\{BRAND\.name\}/g, BRAND.name).replace(/\$\{BRAND\.legalName\}/g, BRAND.legalName),
  }));

  // Preservamos 2 FAQ que dependen de datos de marca en vivo (nombre + razón social).
  faqs.unshift(
    {
      q: `Es seguro enviar con ${BRAND.name}?`,
      a: `Si. ${BRAND.name} es operado por ${BRAND.legalName.toUpperCase()}, empresa formalizada en Chile. Aplicamos verificacion de identidad (KYC), prevencion de fraude y cifrado de datos. Cada envio queda registrado y respaldado. Si en cualquier momento algo no sale como esperabas, te respondemos personalmente y trabajamos contigo hasta resolverlo.`,
    },
  );
  faqs.splice(3, 0, {
    q: `Cuanto cobra ${BRAND.name} por enviar?`,
    a: 'Nuestra comision se aplica directamente en la tasa de cambio. Mientras mas envias, mejor tasa obtienes (escala progresiva). No hay cargos ocultos: lo que ves en la calculadora es lo que tu destinatario recibe.',
  });

  const originFlag = `https://flagcdn.com/w40/${BRAND.landing.origin.code}.png`;
  const destFlag = `https://flagcdn.com/w40/${BRAND.landing.dest.code}.png`;

  const heroSlides = BRAND.landing.hero.map((h, i) => {
    const defaults = [
      {
        imageMobile: 'https://customer-assets.emergentagent.com/job_c52e8a5e-f651-41bf-9b0d-fbd47ef35835/artifacts/blibpsvz_26AD9537-448D-4A84-B0C1-A4B6FF844C64.png',
        imageDesktop: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1600&q=80',
      },
      {
        imageMobile: 'https://customer-assets.emergentagent.com/job_c52e8a5e-f651-41bf-9b0d-fbd47ef35835/artifacts/kbs4ntyq_F92DA6B7-D529-470B-AEF5-73FDF550EE92.png',
        imageDesktop: 'https://images.unsplash.com/photo-1609220136736-443140cffec6?w=1600&q=80',
      },
      {
        imageMobile: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
        imageDesktop: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1600&q=80',
      },
    ];
    return { ...(defaults[i] || defaults[0]), ...h };
  });

  useEffect(() => {
    if (user) navigate('/dashboard');
    setIsVisible(true);

    // Track visita anónima (país/ciudad para analytics admin)
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      fetch(`${backend}/api/analytics/track-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: '/' }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* silent */ }

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => { window.removeEventListener('resize', handleResize); clearInterval(interval); };
  }, [user, navigate, heroSlides.length]);

  const currentHero = heroSlides[currentSlide];

  // ═══════════════════════════════════════════════════════════════════════
  // LAYOUT SPLIT (Fase 7 — solo cuando el preset lo pide, ej. EnviosClick).
  // No renderiza el layout classic. GirosBol nunca entra acá.
  // ═══════════════════════════════════════════════════════════════════════
  if (BRAND.landing.layout === 'split') {
    const iconMap = { Calculator, Send, HeartHandshake, Smartphone, Building2, Sparkles, Zap, Shield };
    const brandFont = BRAND.landing.fontFamily;
    const pm = BRAND.landing.paymentMethods;
    const steps = BRAND.landing.steps || [];
    // SVG de fondo con triángulos morados sutiles (identidad de EnviosClick, patrón geométrico).
    const trianglePattern = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='104' viewBox='0 0 120 104'><g fill='none' stroke='${encodeURIComponent(BRAND.theme.primary)}' stroke-opacity='0.06' stroke-width='1'><polygon points='60,10 110,94 10,94'/><polygon points='60,94 30,42 90,42'/></g></svg>`;

    return (
      <div className="min-h-screen bg-white" style={brandFont ? { fontFamily: brandFont } : {}} data-testid="landing-split">
        {BRAND.landing.fontImport && (
          <link rel="stylesheet" href={BRAND.landing.fontImport} />
        )}
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes floatSlow { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          .ec-fade { animation: fadeUp 0.7s ease-out forwards; }
          .ec-float { animation: floatSlow 6s ease-in-out infinite; }
          .ec-headline { font-family: ${brandFont}; font-weight: 900; letter-spacing: -0.03em; }
          .ec-h2 { font-family: ${brandFont}; font-weight: 800; letter-spacing: -0.02em; }
          .ec-triangles { background-image: url("${trianglePattern}"); background-size: 240px 208px; }
        `}</style>

        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100" data-testid="nav-split">
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
            <img src="/logo.png" alt={BRAND.name} className="h-10 sm:h-12" />
            <div className="flex items-center gap-2">
              <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}`} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full transition" style={{ color: BRAND.theme.primary, border: `1px solid ${BRAND.theme.primary}33` }} data-testid="nav-whatsapp-btn">
                <Smartphone size={14} /> WhatsApp
              </a>
              <button onClick={() => navigate('/login')} className="text-sm font-semibold text-gray-600 px-3 py-2 hover:text-gray-900 transition">
                Iniciar sesión
              </button>
              <button onClick={() => navigate('/register')} className="text-sm font-bold text-white px-5 py-2.5 rounded-full transition hover:opacity-90 shadow-sm" style={{ background: BRAND.landing.ctaButton }} data-testid="nav-register-btn">
                Regístrate
              </button>
            </div>
          </div>
        </nav>

        {/* ═══════════════ SPLIT HERO ═══════════════ */}
        <section className="relative overflow-hidden bg-white">
          {/* Acento gráfico morado sutil — sin degradado de fondo */}
          <div
            className="pointer-events-none absolute -top-24 -right-32 w-[560px] h-[560px] rounded-full"
            style={{ background: `radial-gradient(circle at 30% 30%, ${BRAND.theme.primary}22 0%, transparent 65%)` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full"
            style={{ background: `radial-gradient(circle at 70% 70%, ${BRAND.theme.accent}1a 0%, transparent 65%)` }}
            aria-hidden
          />
          <svg
            className="pointer-events-none absolute top-10 right-6 w-[320px] h-[320px] hidden md:block"
            viewBox="0 0 400 400"
            aria-hidden
          >
            <path
              d="M 60 340 Q 120 180 240 200 T 380 60"
              stroke={BRAND.theme.primary}
              strokeOpacity="0.14"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M 20 260 Q 140 220 200 300"
              stroke={BRAND.theme.accent}
              strokeOpacity="0.12"
              strokeWidth="2"
              fill="none"
            />
          </svg>

          <div className="relative z-10 max-w-6xl mx-auto px-4 pt-8 pb-10 md:py-14 grid lg:grid-cols-[1.1fr_minmax(0,460px)] gap-8 lg:gap-12 items-start" style={{ minHeight: isMobile ? 'auto' : '600px' }}>
            {/* IZQUIERDA: badges + h1 + subtítulo + CTAs + tarjeta foto (abajo) */}
            <div className={isVisible ? 'ec-fade' : 'opacity-0'}>
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <div className="inline-flex items-center gap-2 bg-white rounded-full pl-2 pr-3 py-1.5 shadow-sm border border-gray-200" data-testid="hero-badge-country">
                  <img src={destFlag} alt={BRAND.landing.dest.name} className="w-5 h-auto rounded-sm" />
                  <img src="https://flagcdn.com/w40/bo.png" alt="Bolivia" className="w-5 h-auto rounded-sm -ml-1" />
                  <span className="text-[11px] sm:text-xs font-bold text-gray-800 ml-1">{BRAND.landing.badgeText}</span>
                  <img src={originFlag} alt={BRAND.landing.origin.name} className="w-5 h-auto rounded-sm" />
                </div>
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5" style={{ background: `${BRAND.theme.primary}14`, color: BRAND.theme.primary }}>
                  <Sparkles size={12} />
                  <span className="text-[11px] sm:text-xs font-semibold">{BRAND.landing.familiesBadge}</span>
                </div>
              </div>

              <h1 className="ec-headline text-[38px] sm:text-6xl leading-[1.03] mb-4" style={{ color: '#111111' }}>
                {currentHero.title}
                <br />
                <span
                  data-testid="hero-highlight"
                  style={{
                    color: BRAND.theme.primary,
                    fontWeight: 900,
                  }}
                >
                  {currentHero.highlight}
                </span>
                {currentHero.highlightAfter && (
                  <span style={{ color: '#111111', fontWeight: 900 }}>{currentHero.highlightAfter}</span>
                )}
              </h1>
              <p className="text-base sm:text-lg mb-6 max-w-lg leading-relaxed" style={{ color: '#3A3A3A' }}>
                {currentHero.subtitle}
              </p>

              <div className="flex flex-wrap gap-3 mb-5">
                {/* PRIMARIO: fondo morado, texto blanco */}
                <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}?text=Hola!%20quiero%20cotizar%20un%20env%C3%ADo`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-white font-bold text-base px-6 py-3.5 rounded-full hover:opacity-90 transition shadow-lg" style={{ background: BRAND.theme.primary, boxShadow: `0 15px 35px -12px ${BRAND.theme.primary}80` }} data-testid="hero-whatsapp-btn">
                  <Smartphone size={18} /> Cotizar por WhatsApp
                </a>
                {/* SECUNDARIO: fondo blanco, borde morado, texto morado */}
                <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 bg-white font-semibold px-6 py-3.5 rounded-full transition hover:shadow-md" style={{ border: `2px solid ${BRAND.theme.primary}`, color: BRAND.theme.primary }} data-testid="hero-register-btn">
                  Crear cuenta gratis <ArrowRight size={16} />
                </button>
              </div>

              {/* Trust pills — texto oscuro sobre blanco */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6">
                {BRAND.landing.regulatoryBadge?.enabled && (
                  <div className="flex items-start gap-1.5" style={{ color: '#3A3A3A' }} data-testid="hero-regulatory-badge">
                    <Shield size={13} className="mt-0.5 shrink-0" style={{ color: BRAND.theme.primary }} />
                    <div className="leading-tight">
                      <div className="text-xs font-semibold">{BRAND.landing.regulatoryBadge.label}</div>
                      {BRAND.landing.regulatoryBadge.sublabel && (
                        <div className="text-[10px] font-normal mt-0.5" style={{ color: '#6B6B6B' }}>{BRAND.landing.regulatoryBadge.sublabel}</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#3A3A3A' }}>
                  <Clock size={13} style={{ color: BRAND.theme.primary }} /> 24/7 incluso feriados
                </div>
              </div>

              {/* TARJETA FOTO — carrusel del equipo real como elemento propio, no fondo. */}
              <div className="relative max-w-md" data-testid="hero-photo-card">
                <div className="relative aspect-[4/5] sm:aspect-square rounded-[24px] overflow-hidden shadow-2xl bg-black/20">
                  {heroSlides.map((slide, i) => (
                    <img
                      key={i}
                      src={isMobile ? slide.imageMobile : slide.imageDesktop}
                      alt={slide.title}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                      style={{
                        opacity: i === currentSlide ? 1 : 0,
                        objectPosition: slide.imageObjectPosition || `center ${slide.imageFocusY || '30%'}`,
                      }}
                    />
                  ))}
                  <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 100%)' }} />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <div className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow">
                      <img src="https://flagcdn.com/w40/cl.png" alt="Chile" className="w-3.5 h-auto rounded-sm" />
                      <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: BRAND.theme.primary }}>Equipo Iquique</span>
                    </div>
                    <div className="flex gap-1.5">
                      {heroSlides.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentSlide(i)}
                          className={`h-1.5 rounded-full transition-all ${i === currentSlide ? 'w-5' : 'w-1.5 bg-white/60'}`}
                          style={i === currentSlide ? { background: BRAND.theme.primary } : {}}
                          aria-label={`slide ${i + 1}`}
                          data-testid={`hero-slide-dot-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* DERECHA: calculadora en tarjeta blanca */}
            <div className="relative ec-float lg:mt-0" data-testid="hero-calculator-card">
              <div className="absolute -top-3 -right-3 rounded-full text-[10px] font-black px-3 py-1 shadow-lg z-10" style={{ background: BRAND.theme.accent, color: 'white' }}>
                COTIZA EN 5 SEG
              </div>
              <div className="rounded-[32px] bg-white border border-gray-100" style={{ boxShadow: `0 24px 60px -22px rgba(0,0,0,0.14)` }}>
                <CalculatorLuxury onSendMoney={() => navigate('/register')} />
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ STEPS STRIP ═══════════════ */}
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4 py-10 md:py-14">
            <div className="grid md:grid-cols-3 gap-6 md:gap-10">
              {steps.map((s, i) => {
                const Icon = iconMap[s.icon] || Sparkles;
                return (
                  <div key={i} className="flex items-start gap-4" data-testid={`step-${i + 1}`}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md" style={{ background: `linear-gradient(135deg, ${BRAND.theme.primary} 0%, ${BRAND.theme.accent} 100%)` }}>
                      <Icon size={22} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="ec-h2 text-[11px] font-black uppercase tracking-widest" style={{ color: BRAND.theme.primary }}>0{i + 1}</span>
                        <h3 className="ec-h2 text-lg text-gray-900">{s.title}</h3>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════ PAYMENT METHODS PERÚ ═══════════════ */}
        <section className="relative ec-triangles" style={{ background: BRAND.theme.backgroundAlt || '#F5F4F7' }}>
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-20 relative">
            <div className="max-w-2xl mb-10">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4" style={{ background: `${BRAND.theme.primary}12`, color: BRAND.theme.primary }}>
                <img src="https://flagcdn.com/w40/pe.png" alt="Perú" className="w-4 h-auto rounded-sm" />
                <span className="text-[11px] font-black uppercase tracking-widest">Métodos en Perú</span>
              </div>
              <h2 className="ec-h2 text-3xl sm:text-4xl text-gray-900 mb-3">{pm.title}</h2>
              <p className="text-gray-500 text-base leading-relaxed">{pm.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {pm.items.map((m, i) => (
                <div key={i} className="bg-white rounded-3xl p-6 hover:-translate-y-1 transition-transform shadow-[0_10px_40px_-15px_rgba(0,0,0,0.15)]" data-testid={`payment-method-${m.code}`}>
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: `${m.color}12` }}>
                      {m.logo ? (
                        <img src={m.logo} alt={m.name} className="w-10 h-10 object-contain" />
                      ) : (
                        <Building2 size={28} style={{ color: m.color }} />
                      )}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: `${m.color}18`, color: m.color }}>
                      {m.badge}
                    </span>
                  </div>
                  <h3 className="ec-h2 text-xl text-gray-900 mb-2">{m.name}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SOBRE NOSOTROS / EQUIPO ═══════════════ */}
        {BRAND.landing.about?.enabled && (
        <section className="bg-white" data-testid="about-section">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
              {/* Texto */}
              <div>
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-5" style={{ background: `${BRAND.theme.primary}12`, color: BRAND.theme.primary }}>
                  <img src="https://flagcdn.com/w40/cl.png" alt="Chile" className="w-4 h-auto rounded-sm" />
                  <span className="text-[11px] font-black uppercase tracking-widest">{BRAND.landing.about.badge}</span>
                </div>
                <h2 className="ec-headline text-3xl sm:text-[42px] text-gray-900 mb-5 leading-[1.05]">
                  {BRAND.landing.about.title}
                </h2>
                <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-8">
                  {BRAND.landing.about.paragraph}
                </p>
                <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-8">
                  {BRAND.landing.about.stats.map((s, i) => (
                    <div key={i} className="border-l-2 pl-3" style={{ borderColor: BRAND.theme.primary }}>
                      <p className="ec-h2 text-2xl sm:text-3xl text-gray-900">{s.value}</p>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
                <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}?text=Hola!%20me%20gustar%C3%ADa%20conocer%20m%C3%A1s%20de%20${BRAND.name}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-bold text-sm hover:underline" style={{ color: BRAND.theme.primary }} data-testid="about-whatsapp-btn">
                  Habla con nuestro equipo <ArrowRight size={16} />
                </a>
              </div>

              {/* Collage de fotos reales */}
              <div className="grid grid-cols-6 grid-rows-6 gap-3 sm:gap-4 h-[420px] sm:h-[520px]">
                {/* Foto principal — equipo en Iquique */}
                <picture className="col-span-6 row-span-4 relative overflow-hidden rounded-[28px] shadow-xl group">
                  <source media="(max-width: 640px)" srcSet={BRAND.landing.about.featurePhoto.webpSm} type="image/webp" />
                  <source srcSet={BRAND.landing.about.featurePhoto.webp} type="image/webp" />
                  <source media="(max-width: 640px)" srcSet={BRAND.landing.about.featurePhoto.jpgSm} type="image/jpeg" />
                  <img src={BRAND.landing.about.featurePhoto.jpg} alt={BRAND.landing.about.featurePhoto.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" style={{ objectPosition: 'center 30%' }} data-testid="about-photo-feature" />
                  <div className="absolute inset-x-0 bottom-0 h-20 pointer-events-none" style={{ background: `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 100%)` }} />
                  <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                    <span className="text-sm font-bold drop-shadow">{BRAND.landing.about.featurePhoto.caption}</span>
                    <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: BRAND.theme.accent }}>
                      <Sparkles size={16} className="text-white" />
                    </span>
                  </div>
                </picture>
                {/* Retratos */}
                {BRAND.landing.about.portraitPhotos.map((p, i) => (
                  <picture key={i} className="col-span-3 row-span-2 relative overflow-hidden rounded-[22px] shadow-lg group">
                    <source media="(max-width: 640px)" srcSet={p.webpSm} type="image/webp" />
                    <source srcSet={p.webp} type="image/webp" />
                    <source media="(max-width: 640px)" srcSet={p.jpgSm} type="image/jpeg" />
                    <img src={p.jpg} alt={p.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700" style={{ objectPosition: 'center 25%' }} data-testid={`about-photo-portrait-${i}`} />
                    <div className="absolute inset-x-0 bottom-0 h-14 pointer-events-none" style={{ background: `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.65) 100%)` }} />
                    <div className="absolute bottom-2 left-3 right-3 text-white text-[11px] font-bold leading-tight drop-shadow">
                      {p.caption}
                    </div>
                  </picture>
                ))}
              </div>
            </div>
          </div>
        </section>
        )}

        {/* ═══════════════ POR QUÉ ELEGIRNOS ═══════════════ */}
        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-20">
            <div className="max-w-2xl mb-10">
              <h2 className="ec-h2 text-3xl sm:text-4xl text-gray-900 mb-3">Por qué {BRAND.name}</h2>
              <p className="text-gray-500">Lo que nos hace diferentes en cada envío.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { icon: Zap, title: 'Yape y Plin al instante', desc: 'Tu familia en Perú recibe en segundos con solo su número de celular.' },
                {
                  icon: Shield,
                  title: BRAND.landing.regulatoryBadge?.whyUsTitle || 'Empresa registrada ante la UAF',
                  desc: BRAND.landing.regulatoryBadge?.whyUsDesc || 'Sujeto obligado conforme a la Ley 19.913 de Chile. Reportamos operaciones sospechosas a la Unidad de Análisis Financiero según la normativa vigente.',
                },
                { icon: Clock, title: 'Todos los días, todo el año', desc: 'Fines de semana y feriados incluidos. WhatsApp 24/7 con persona real.' },
                { icon: HeartHandshake, title: 'Comprobante al celular', desc: 'Pantallazo con confirmación directo a tu WhatsApp cuando el dinero llega.' },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex items-start gap-4 rounded-3xl p-6 border border-gray-100 hover:border-gray-200 transition" style={{ background: BRAND.theme.backgroundAlt || '#F5F4F7' }}>
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'white', color: BRAND.theme.primary, boxShadow: `0 6px 20px -8px ${BRAND.theme.primary}66` }}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <h3 className="ec-h2 text-base text-gray-900 mb-1.5">{f.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════ FAQ ═══════════════ */}
        <section className="ec-triangles" style={{ background: BRAND.theme.backgroundAlt || '#F5F4F7' }}>
          <div className="max-w-3xl mx-auto px-4 py-16 md:py-20">
            <h2 className="ec-h2 text-3xl sm:text-4xl text-gray-900 text-center mb-3">Preguntas frecuentes</h2>
            <p className="text-gray-500 text-center mb-10">Todo lo que necesitas saber antes del primer envío.</p>
            <div className="space-y-3">
              {faqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className={`bg-white rounded-2xl overflow-hidden transition-all ${isOpen ? 'shadow-lg' : 'shadow-sm hover:shadow-md'}`} style={isOpen ? { boxShadow: `0 20px 50px -20px ${BRAND.theme.primary}40` } : {}} data-testid={`faq-item-${i}`}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" data-testid={`faq-toggle-${i}`}>
                      <span className="font-bold text-gray-900 text-[15px] sm:text-base">{f.q}</span>
                      <ChevronDown size={20} className="flex-shrink-0 transition-transform" style={{ color: BRAND.theme.primary, transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8">
              ¿Otra duda?{' '}
              <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}?text=Hola!%20Tengo%20una%20duda`} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:opacity-80" style={{ color: BRAND.theme.primary }} data-testid="faq-whatsapp-link">
                Escríbenos por WhatsApp
              </a>
            </p>
          </div>
        </section>

        {/* ═══════════════ FINAL CTA ═══════════════ */}
        <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.landing.ctaButton} 0%, ${BRAND.landing.ctaButtonDark} 100%)` }}>
          <div className="absolute inset-0 ec-triangles opacity-30" />
          <div className="max-w-4xl mx-auto px-4 py-20 md:py-24 text-center relative z-10">
            <h2 className="ec-headline text-white text-4xl sm:text-5xl mb-5">Empieza a enviar hoy</h2>
            <p className="text-white/90 mb-10 text-lg max-w-xl mx-auto leading-relaxed">{BRAND.landing.finalCtaText}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 bg-white font-bold text-lg px-10 py-4 rounded-full hover:shadow-2xl transition shadow-xl" style={{ color: BRAND.landing.ctaButton }} data-testid="cta-register-btn">
                Crear cuenta gratis <ArrowRight size={20} />
              </button>
              <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}?text=Hola!%20quiero%20cotizar%20un%20env%C3%ADo`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-white/50 text-white font-semibold text-lg px-8 py-4 rounded-full hover:bg-white/10 transition" data-testid="cta-whatsapp-btn">
                <Smartphone size={20} /> WhatsApp
              </a>
            </div>
            <p className="text-white/70 text-xs mt-6">Sin compromisos · Sin costo · Atención humana</p>
          </div>
        </section>

        {/* ═══════════════ FOOTER ═══════════════ */}
        <footer className="bg-slate-950 text-gray-300 pt-14 pb-8">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-10">
              <img src="/logo.png" alt={BRAND.name} className="h-12 w-auto mb-5" style={{ filter: 'brightness(0) invert(1)' }} data-testid="footer-logo" />
              <p className="text-gray-400 text-sm sm:text-base max-w-xl leading-relaxed mb-6">
                {BRAND.landing.footerTagline}
              </p>
              <div className="flex items-center gap-3">
                {BRAND.landing.social?.instagram && (
                  <a href={BRAND.landing.social.instagram.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:scale-105 transition shadow-lg" style={{ background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)' }} aria-label="Instagram" data-testid="footer-instagram">
                    <span className="w-8 h-8 rounded-full bg-white/95 flex items-center justify-center">
                      <Instagram size={16} className="text-pink-600" />
                    </span>
                    <span className="text-white text-xs font-bold">@{BRAND.landing.social.instagram.handle}</span>
                  </a>
                )}
                {BRAND.landing.social?.facebook && (
                  <a href={BRAND.landing.social.facebook.url} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center hover:scale-110 transition shadow-lg" aria-label="Facebook" data-testid="footer-facebook">
                    <Facebook size={18} className="text-white" />
                  </a>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 border-t border-gray-800 pt-10">
              <div>
                <h3 className="font-bold text-base mb-4" style={{ color: BRAND.theme.accent }}>Servicios</h3>
                <ul className="space-y-3 text-sm">
                  {BRAND.landing.footerServices.map((svc, i) => (
                    <li key={i}>
                      <button onClick={() => navigate(svc.to)} className="hover:text-white transition text-left">{svc.label}</button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-base mb-4" style={{ color: BRAND.theme.accent }}>Soporte</h3>
                <ul className="space-y-3 text-sm">
                  <li>WhatsApp: <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">{BRAND.landing.footerWhatsAppDisplay}</a></li>
                  <li className="text-gray-400">contacto@enviosclick.cl</li>
                  <li className="text-gray-400">Atención 24/7 · Feriados incluidos</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-base mb-4" style={{ color: BRAND.theme.accent }}>Legal</h3>
                <ul className="space-y-3 text-sm">
                  <li><button onClick={() => navigate('/terminos')} className="hover:text-white transition text-left" data-testid="footer-terminos">Términos y condiciones</button></li>
                  <li><button onClick={() => navigate('/privacidad')} className="hover:text-white transition text-left" data-testid="footer-privacidad">Política de privacidad</button></li>
                  <li><button onClick={() => navigate('/denuncias')} className="hover:text-white transition text-left" data-testid="footer-denuncias">Canal de denuncias</button></li>
                  <li className="text-gray-400">Cumplimiento KYC/UAF</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-10 pt-6 text-center text-xs text-gray-500">
              <p>&copy; 2026 {BRAND.name}. Todos los derechos reservados.</p>
            </div>
          </div>
        </footer>
      </div>
    );
  }
  // ═══════════════════════ FIN LAYOUT SPLIT ═══════════════════════

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse-soft { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }
        .fade-up { animation: fadeUp 0.7s ease-out forwards; }
        .fade-up-d1 { animation: fadeUp 0.7s ease-out 0.15s forwards; opacity: 0; }
        .fade-up-d2 { animation: fadeUp 0.7s ease-out 0.3s forwards; opacity: 0; }
        .pulse-soft { animation: pulse-soft 3s ease-in-out infinite; }
        .hero-bg { transition: background-image 1s ease-in-out; }
      `}</style>

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo.png" alt={`${BRAND.name}`} className="h-10 sm:h-12" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/empresas/registro')}
              className="hidden sm:inline-flex text-xs font-bold text-emerald-700 border border-emerald-300 hover:bg-emerald-50 px-3 py-2 rounded-full transition"
              data-testid="nav-empresas-btn"
              title="¿Tienes una empresa? Regístrala con comisión preferente"
            >
              🏢 Empresas
            </button>
            <button onClick={() => navigate('/login')} className="text-sm font-semibold text-gray-600 px-3 py-2 hover:text-gray-900 transition">
              Iniciar sesion
            </button>
            <button onClick={() => navigate('/register')} className="text-sm font-bold text-white px-4 py-2 rounded-full transition hover:opacity-90" style={{ background: BRAND.landing.ctaButton }} data-testid="nav-register-btn">
              Registrate
            </button>
          </div>
        </div>
      </nav>

      {/* ===== HERO WITH CAROUSEL ===== */}
      <section className="relative overflow-hidden" style={{ minHeight: isMobile ? '520px' : '560px' }}>
        {/* Background image carousel */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${isMobile ? slide.imageMobile : slide.imageDesktop})`,
              backgroundPosition: 'center center',
              opacity: i === currentSlide ? 1 : 0,
            }}
          />
        ))}
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-10 pb-16 md:pt-16 md:pb-20 flex flex-col justify-end" style={{ minHeight: isMobile ? '520px' : '560px' }}>
          <div className={isVisible ? 'fade-up' : 'opacity-0'}>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg" data-testid="hero-badge-country">
                <img src={destFlag} alt={BRAND.landing.dest.name} className="w-5 h-auto rounded-sm" />
                <span className="text-xs font-bold text-gray-800">{BRAND.landing.badgeText}</span>
                <img src={originFlag} alt={BRAND.landing.origin.name} className="w-5 h-auto rounded-sm" />
              </div>
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: BRAND.landing.heroHighlight }}></div>
                <span className="text-white/90 text-xs font-semibold" style={{ color: BRAND.landing.heroHighlight === '#4ade80' ? '#bbf7d0' : undefined }}>{BRAND.landing.familiesBadge}</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-3">
              <span className="text-white block mb-1">{currentHero.title}</span>
              <span className="block" style={{ color: BRAND.landing.heroHighlight }}>{currentHero.highlight}</span>
            </h1>
            <p className="text-white/80 text-base sm:text-lg mb-6 max-w-md">
              {currentHero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <button
                onClick={() => navigate('/register')}
                className="flex items-center justify-center gap-2 bg-white font-bold text-lg px-8 py-4 rounded-full hover:bg-gray-50 transition shadow-lg pulse-soft"
                style={{ color: BRAND.landing.ctaButton }}
                data-testid="hero-register-btn"
              >
                Enviar ahora <ArrowRight size={20} />
              </button>
              <button
                onClick={() => document.getElementById('calculadora')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-6 py-4 rounded-full hover:bg-white/10 transition"
              >
                Ver tasa de cambio
              </button>
            </div>
            {/* Trust + slide dots */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-white/70 text-xs"><Shield size={13} /> Legal</div>
                <div className="flex items-center gap-1.5 text-white/70 text-xs"><Zap size={13} /> En minutos</div>
                {BRAND.landing.showRetentionPill && (
                  <div className="flex items-center gap-1.5 text-white/70 text-xs"><Users size={13} /> {BRAND.landing.retentionPillText}</div>
                )}
              </div>
              <div className="flex gap-1.5">
                {heroSlides.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`h-2 rounded-full transition-all ${i === currentSlide ? 'w-5' : 'w-2 bg-white/40'}`} style={i === currentSlide ? { background: BRAND.landing.heroHighlight } : {}} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CALCULATOR SECTION ===== */}
      <section id="calculadora" className="py-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-green-200/30 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-5xl mx-auto px-4 relative">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Calcula tu envio</h2>
            <p className="text-gray-500 text-sm">Tasa en tiempo real. Sin sorpresas.</p>
          </div>

          <div className="grid md:grid-cols-[1fr_minmax(0,440px)] gap-6 md:gap-10 items-center">
            {/* Mascota + Bocadillo (Desktop) */}
            <div className="hidden md:flex flex-col items-center justify-center relative">
              <div className="relative">
                {/* Glow detrás */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-300/40 to-purple-300/40 blur-3xl rounded-full"></div>
                <img
                  src="/mascot.png"
                  alt={`${BRAND.name} - Envía dinero a ${BRAND.landing.dest.name}`}
                  className="relative w-72 lg:w-80 drop-shadow-2xl animate-bounce-slow"
                  data-testid="mascot-calculator-desktop"
                />
                {/* Bocadillo de diálogo */}
                <div className="absolute -top-2 -right-6 lg:-right-10 bg-white rounded-2xl shadow-xl px-4 py-3 max-w-[200px] transform rotate-2" style={{ border: `2px solid ${BRAND.theme.primary}` }}>
                  <p className="text-sm font-bold text-gray-800 leading-tight">
                    Hola! Calcula <span style={{ color: BRAND.theme.primary }}>aqui</span> cuanto recibe tu familia <span style={{ color: BRAND.theme.accent }}>en segundos</span>
                  </p>
                  {/* Cola del bocadillo */}
                  <div className="absolute -bottom-2 left-6 w-4 h-4 bg-white transform rotate-45" style={{ borderRight: `2px solid ${BRAND.theme.primary}`, borderBottom: `2px solid ${BRAND.theme.primary}` }}></div>
                </div>
              </div>
              {/* Trust pills bajo mascota */}
              <div className="mt-6 flex flex-col gap-2 items-center">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-100">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-xs font-bold text-gray-700">Tasa real de mercado</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-100">
                  <Zap size={16} className="text-amber-500" />
                  <span className="text-xs font-bold text-gray-700">Acreditacion en minutos</span>
                </div>
              </div>
            </div>

            {/* Calculadora */}
            <div className="relative">
              {/* Mascota peek mobile */}
              <div className="md:hidden absolute -top-12 -right-2 z-10 pointer-events-none">
                <img
                  src="/mascot.png"
                  alt={`${BRAND.name} - Envía dinero a ${BRAND.landing.dest.name}`}
                  className="w-24 drop-shadow-xl animate-bounce-slow"
                  data-testid="mascot-calculator-mobile"
                />
              </div>
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border">
                <CalculatorLuxury onSendMoney={() => navigate('/register')} />
              </div>
              <p className="text-center text-xs text-green-700 font-semibold mt-3">
                Entre mas envies, mejor cambio obtienes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF ===== */}
      <section className="py-8 bg-white border-y">
        <div className="max-w-4xl mx-auto px-4">
          <div className={`grid gap-4 text-center ${BRAND.landing.stats.length === 3 ? 'grid-cols-3' : BRAND.landing.stats.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {BRAND.landing.stats.map((s, i) => (
              <div key={i}>
                <p className="text-2xl sm:text-3xl font-black text-gray-900">
                  {s.value}
                  {s.suffix && <span style={{ color: BRAND.theme.primary }}>{s.suffix}</span>}
                </p>
                <p className="text-xs text-gray-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3 STEPS WITH MASCOT ===== */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Decorative blob */}
        <div className="absolute top-20 right-0 w-72 h-72 bg-green-100/40 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-6xl mx-auto px-4 relative">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 text-center mb-2">Asi de facil</h2>
          <p className="text-gray-500 text-center mb-10 text-sm">3 pasos y tu dinero llega</p>

          <div className="grid md:grid-cols-[280px_1fr] gap-8 md:gap-12 items-center">
            {/* Mascota */}
            <div className="hidden md:flex justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-300/40 to-purple-300/40 blur-3xl rounded-full"></div>
              <img
                src="/mascot.png"
                alt={`${BRAND.name} - Calcula tu envío a ${BRAND.landing.dest.name}`}
                className="relative w-64 lg:w-72 drop-shadow-2xl animate-bounce-slow"
                data-testid="mascot-howto"
              />
            </div>

            {/* Pasos */}
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { step: '1', icon: <Users size={24} />, title: 'Registrate', desc: '5 minutos desde tu celular. Solo necesitas tu carnet y una selfie.', color: 'from-green-500 to-green-700' },
                { step: '2', icon: <Zap size={24} />, title: 'Elige monto y destino', desc: 'Calcula tu envio con tasa en tiempo real. Sin sorpresas.', color: 'from-emerald-500 to-emerald-700' },
                { step: '3', icon: <CheckCircle size={24} />, title: 'Listo, llega en minutos', desc: 'Pagas por transferencia y tu destinatario recibe al tiro.', color: 'from-teal-500 to-teal-700' },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-5 border-2 border-gray-100 hover:border-green-400 hover:shadow-xl transition-all relative group"
                  style={{ animationDelay: `${i * 100}ms` }}
                  data-testid={`step-card-${i+1}`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 text-white bg-gradient-to-br ${s.color} group-hover:scale-110 transition-transform shadow-lg`}>
                    {s.icon}
                  </div>
                  <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-black text-gray-500">{s.step}</div>
                  <h3 className="text-base font-bold text-gray-900 mb-1.5">{s.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 text-white font-bold px-8 py-4 rounded-full text-lg transition hover:opacity-90 shadow-lg" style={{ background: BRAND.landing.ctaButton }} data-testid="steps-register-btn">
              Registrate gratis <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ===== COUNTRIES ===== */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-black text-gray-900 text-center mb-6">Enviamos a {BRAND.landing.countries.length} paises</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {BRAND.landing.countries.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-white border rounded-xl px-4 py-3 hover:bg-gray-50 transition relative" style={{ borderColor: '#e5e7eb' }}>
                <img src={`https://flagcdn.com/w40/${c.code}.png`} alt={c.name} className="w-7 h-auto rounded-sm shadow-sm" />
                <span className="font-bold text-gray-800 text-sm">{c.name}</span>
                {c.tag && <span className="absolute -top-2 -right-2 text-white text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: BRAND.theme.primary }}>{c.tag}</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY {BRAND.name.toUpperCase()} ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 text-center mb-2">Por que {BRAND.name}</h2>
          <p className="text-gray-500 text-center mb-10 text-sm">Lo que nos hace diferentes</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: <Zap size={22} className="text-green-600" />, title: 'Rapido', desc: 'Tu dinero llega en minutos, no en dias.' },
              { icon: <Shield size={22} className="text-green-600" />, title: 'Seguro y legal', desc: 'Cumplimos toda la normativa chilena. Sin riesgos.' },
              { icon: <Star size={22} className="text-green-600" />, title: 'Mejor cambio', desc: 'Entre mas envies, mejor tasa obtienes. En tiempo real.' },
              { icon: <Clock size={22} className="text-green-600" />, title: 'Soporte humano', desc: 'Te ayudamos por WhatsApp. Personas reales, no robots.' },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">{f.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-500">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      {BRAND.landing.testimonials.length > 0 && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 text-center mb-2">Lo que dicen nuestros clientes</h2>
          <p className="text-gray-500 text-center mb-8 text-sm">Personas reales, envios reales</p>
          <div className="grid md:grid-cols-3 gap-4">
            {BRAND.landing.testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border">
                <div className="flex gap-0.5 mb-3">
                  {[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-yellow-500 fill-yellow-500" />)}
                </div>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: BRAND.theme.primary }}>{t.name[0]}</div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ===== SECURITY ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Shield size={32} className="text-green-700" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-3">Tu dinero esta protegido</h2>
          <p className="text-gray-500 max-w-lg mx-auto mb-8 text-sm leading-relaxed">
            {BRAND.name} opera bajo la normativa chilena. Verificamos la identidad de cada usuario para prevenir fraudes. Tu dinero llega seguro, siempre.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Verificacion KYC', 'Cifrado de datos', 'Prevencion de fraude', 'Soporte real'].map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-full px-4 py-2 border text-sm text-gray-700">
                <CheckCircle size={14} className="text-green-600" /> {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 text-center mb-2">Preguntas frecuentes</h2>
          <p className="text-gray-500 text-center mb-10 text-sm">Resolvemos tus dudas en segundos</p>

          <div className="space-y-3">
            {faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${isOpen ? 'border-green-500 shadow-md' : 'border-gray-100 hover:border-gray-200'}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-bold text-gray-900 text-sm sm:text-base">{f.q}</span>
                    <ChevronDown
                      size={20}
                      className={`flex-shrink-0 text-green-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Tienes otra duda?{' '}
            <a
              href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}?text=Hola!%20Tengo%20una%20duda%20sobre%20${BRAND.name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline hover:opacity-80"
              style={{ color: BRAND.theme.primary }}
              data-testid="faq-whatsapp-link"
            >
              Escribenos por WhatsApp
            </a>
          </p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.landing.ctaButton} 0%, ${BRAND.landing.ctaButtonDark} 100%)` }}>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Empieza a enviar hoy</h2>
          <p className="text-white/80 mb-8 text-base max-w-md mx-auto">{BRAND.landing.finalCtaText}</p>
          <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 bg-white font-bold text-lg px-10 py-4 rounded-full hover:bg-gray-50 transition shadow-xl pulse-soft" style={{ color: BRAND.landing.ctaButton }} data-testid="cta-register-btn">
            Crear mi cuenta gratis <ArrowRight size={20} />
          </button>
          <p className="text-white/60 text-xs mt-4">Sin compromisos. Sin costo.</p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-slate-950 text-gray-300 pt-14 pb-8">
        <div className="max-w-6xl mx-auto px-6">
          {/* Logo + Tagline + Social */}
          <div className="mb-10">
            <img
              src="/logo.png"
              alt={`${BRAND.name}`}
              className="h-12 sm:h-14 w-auto mb-5"
              style={{ filter: 'brightness(0) invert(1)' }}
              data-testid="footer-logo"
            />
            <p className="text-gray-400 text-sm sm:text-base max-w-xl leading-relaxed mb-6">
              {BRAND.landing.footerTagline}
            </p>
            <div className="flex items-center gap-3">
              <a
                href={`https://instagram.com/${BRAND.name.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 transition shadow-lg"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={20} className="text-white" />
              </a>
              <a
                href={`https://facebook.com/${BRAND.name.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center hover:scale-110 transition shadow-lg"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook size={20} className="text-white" />
              </a>
              <a
                href={`https://www.tiktok.com/@${BRAND.name.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-black border border-gray-700 flex items-center justify-center hover:scale-110 transition shadow-lg"
                aria-label="TikTok"
                data-testid="footer-tiktok"
              >
                <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.71a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.14z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Columnas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 border-t border-gray-800 pt-10">
            {/* Servicios */}
            <div>
              <h3 className="text-blue-400 font-bold text-base mb-4">Servicios</h3>
              <ul className="space-y-3 text-sm">
                {BRAND.landing.footerServices.map((svc, i) => (
                  <li key={i}>
                    <button onClick={() => navigate(svc.to)} className="hover:text-white transition text-left">{svc.label}</button>
                  </li>
                ))}
                <li>
                  <button onClick={() => document.getElementById('calculadora')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition text-left">Calculadora de tasas</button>
                </li>
              </ul>
            </div>

            {/* Soporte */}
            <div>
              <h3 className="text-blue-400 font-bold text-base mb-4">Soporte</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  WhatsApp: <a href={`https://wa.me/${BRAND.landing.footerWhatsAppNumber}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">{BRAND.landing.footerWhatsAppDisplay}</a>
                </li>
                <li className="text-gray-400">Atencion personalizada</li>
                <li className="text-gray-400">Ayuda paso a paso</li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-blue-400 font-bold text-base mb-4">Legal</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <button onClick={() => navigate('/terminos')} className="hover:text-white transition text-left" data-testid="footer-terminos">Terminos y condiciones</button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacidad')} className="hover:text-white transition text-left" data-testid="footer-privacidad">Politica de privacidad</button>
                </li>
                <li>
                  <button onClick={() => navigate('/denuncias')} className="hover:text-white transition text-left" data-testid="footer-denuncias">Canal de denuncias</button>
                </li>
                <li>
                  <button onClick={() => navigate('/empresas/registro')} className="text-emerald-400 hover:text-emerald-300 font-semibold transition text-left" data-testid={`footer-empresas-link-${BRAND.name.toLowerCase()}`}>
                    🏢 Cuenta Empresa
                  </button>
                </li>
                <li className="text-gray-400">Cumplimiento KYC/AML</li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 mt-10 pt-6 text-center text-xs text-gray-500">
            <p>&copy; 2026 {BRAND.name}. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingBrand;
